import get from "lodash/get";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import AccessRequestsTable from "./components/AccessRequestsTable";

const AccessRequests = ({ pendingClients }) => {
  const { t } = useTranslation("coaching-portal");
  const [sortConfig, setSortConfig] = useState({
    key: "addedAt",
    direction: "descending",
  });
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());

  const handleSort = ({ target }, direction = "ascending") => {
    setSortConfig({ key: target.value, direction });
  };

  const sortedClients = useMemo(() => {
    let filtered = [...pendingClients];

    // Filter by search
    if (search) {
      const term = search.toLowerCase().trim();
      filtered = filtered.filter(
        (client) =>
          String(client.name).toLowerCase().includes(term) ||
          String(client.email).toLowerCase().includes(term)
      );
    }

    // Sort
    if (sortConfig) {
      filtered.sort((a, b) => {
        const aVal = get(a, sortConfig.key);
        const bVal = get(b, sortConfig.key);
        if (aVal < bVal) return sortConfig.direction === "ascending" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "ascending" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [pendingClients, sortConfig, search]);

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    if (selectedIds.size === sortedClients.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedClients.map((c) => c._id)));
    }
  };

  const handleBulkAccept = () => {
    if (
      !confirm(
        t("accessRequests.bulkAcceptConfirm", { count: selectedIds.size })
      )
    ) {
      return;
    }

    const clientIds = Array.from(selectedIds);
    submit("/coaching-portal/access-requests/bulk-approve", {
      method: "POST",
      body: {
        clientIds,
        type: "perform",
      },
    });
  };

  return (
    <main className="access-requests">
      <h1>{t("accessRequests.pageTitle")}</h1>

      <header>
        <hgroup>
          <input
            type="search"
            className="text-input"
            name="search"
            placeholder={t("accessRequests.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-expandable="true"
          />
        </hgroup>
        {selectedIds.size > 0 && (
          <hgroup className="bulk-action-bar">
            <span>
              {t("accessRequests.selectedCount", { count: selectedIds.size })}
            </span>
            <button
              data-button-type="primary"
              onClick={handleBulkAccept}
              type="button"
            >
              {t("accessRequests.acceptAllButton")}
            </button>
          </hgroup>
        )}
      </header>

      <AccessRequestsTable
        clients={sortedClients}
        sortConfig={sortConfig}
        handleSort={handleSort}
        selectedIds={selectedIds}
        onToggleSelect={handleToggleSelect}
        onToggleSelectAll={handleToggleSelectAll}
      />
    </main>
  );
};

export default AccessRequests;
